import { AnalyticsConfig } from '../mm/types/analytics.types';
import { 
  getAvailableTemplates, 
  getTemplateMetadata,
  getTemplatesByCategory as getTemplatesByCategoryFromLoader,
  getTemplateCategories as getTemplateCategoriesFromLoader,
  getTemplateOptions as getTemplateOptionsFromLoader,
  loadTemplate,
  validateTemplate as validateTemplateFromLoader,
  type TemplateMetadata 
} from './template-loader';

export interface DashboardTemplate {
  id: string;
  label: string;
  description: string;
  category: string;
  config?: AnalyticsConfig; // Optional since we load on demand
  previewImage?: string;
  tags?: string[];
}

/**
 * Registry of available dashboard templates
 * Uses React Admin's data provider pattern for consistent API calls
 */
class DashboardTemplateRegistry {
  private templates: Map<string, DashboardTemplate> = new Map();
  private configCache: Map<string, AnalyticsConfig> = new Map();
  private metadataLoaded: boolean = false;
  private metadataLoading: Promise<void> | null = null;

  constructor() {
    // Templates are now loaded on-demand when first accessed
  }

  /**
   * Load template metadata from server - only when needed
   * Uses React Admin's data provider pattern for consistent behavior
   */
  private async loadTemplateMetadata(): Promise<void> {
    if (this.metadataLoaded) {
      return;
    }
    
    if (this.metadataLoading) {
      return this.metadataLoading;
    }
    
    this.metadataLoading = this._doLoadTemplateMetadata();
    await this.metadataLoading;
  }
  
  private async _doLoadTemplateMetadata(): Promise<void> {
    try {
      console.log(`🔍 [DashboardTemplateRegistry] Loading template metadata`);
      
      const availableTemplates = await getAvailableTemplates();
      
      availableTemplates.forEach(template => {
        this.templates.set(template.id, {
          id: template.id,
          label: template.name,
          description: template.description,
          category: template.category,
          tags: template.tags,
          // config will be loaded on demand
        });
      });
      
      this.metadataLoaded = true;
      console.log(`✅ [DashboardTemplateRegistry] Loaded ${availableTemplates.length} template metadata entries`);
      
    } catch (error) {
      console.error(`❌ [DashboardTemplateRegistry] Failed to load template metadata:`, error);
      this.metadataLoading = null; // Allow retry
      throw error;
    }
  }

  /**
   * Get template by ID with on-demand loading of configuration
   * Ensures metadata is loaded before access
   */
  async getTemplate(templateId: string): Promise<DashboardTemplate | undefined> {
    // Ensure metadata is loaded first
    await this.loadTemplateMetadata();
    
    const template = this.templates.get(templateId);
    if (!template) {
      return undefined;
    }

    // Load configuration if not already cached
    if (!template.config && !this.configCache.has(templateId)) {
      const config = await loadTemplate(templateId);
      if (config) {
        this.configCache.set(templateId, config);
        template.config = config;
      }
    } else if (this.configCache.has(templateId)) {
      template.config = this.configCache.get(templateId);
    }

    return template;
  }

  /**
   * Get all available templates (metadata only)
   * Ensures metadata is loaded before access
   */
  async getTemplates(): Promise<Record<string, DashboardTemplate>> {
    // Ensure metadata is loaded first
    await this.loadTemplateMetadata();
    
    const templates: Record<string, DashboardTemplate> = {};
    this.templates.forEach((template, id) => {
      templates[id] = template;
    });
    return templates;
  }

  /**
   * Get templates by category
   * Ensures metadata is loaded before access
   */
  async getTemplatesByCategory(category: string): Promise<DashboardTemplate[]> {
    // Ensure metadata is loaded first
    await this.loadTemplateMetadata();
    
    return Array.from(this.templates.values()).filter(
      template => template.category === category
    );
  }

  /**
   * Get all available categories
   */
  async getTemplateCategories(): Promise<string[]> {
    return await getTemplateCategoriesFromLoader();
  }

  /**
   * Get template options for UI dropdowns
   */
  async getTemplateOptions(): Promise<Array<{ id: string; name: string }>> {
    return await getTemplateOptionsFromLoader();
  }

  /**
   * Search templates by query
   * Ensures metadata is loaded before access
   */
  async searchTemplates(query: string): Promise<DashboardTemplate[]> {
    // Ensure metadata is loaded first
    await this.loadTemplateMetadata();
    
    const searchTerm = query.toLowerCase();
    return Array.from(this.templates.values()).filter(template =>
      template.label.toLowerCase().includes(searchTerm) ||
      template.description.toLowerCase().includes(searchTerm) ||
      template.tags?.some(tag => tag.toLowerCase().includes(searchTerm))
    );
  }

  /**
   * Validate a template configuration
   */
  validateTemplate(config: AnalyticsConfig): boolean {
    return validateTemplateFromLoader(config);
  }

  /**
   * Refresh template metadata (useful for hot reloading in development)
   * Reset state to allow fresh loading on next access
   */
  refresh(): void {
    this.templates.clear();
    this.configCache.clear();
    this.metadataLoaded = false;
    this.metadataLoading = null;
    // Templates will be loaded when next accessed
  }
}

// Create singleton instance
const registry = new DashboardTemplateRegistry();

/**
 * Legacy compatibility: Export as the old DASHBOARD_TEMPLATES object
 * This maintains backward compatibility with existing code
 */
export const DASHBOARD_TEMPLATES = registry.getTemplates();

/**
 * Get template options formatted for React Admin SelectInput
 */
export const getTemplateOptions = () => registry.getTemplateOptions();

/**
 * Get template by ID (async due to lazy loading)
 */
export const getTemplate = (templateId: string) => registry.getTemplate(templateId);

/**
 * Get templates by category
 */
export const getTemplatesByCategory = (category: string) => registry.getTemplatesByCategory(category);

/**
 * Get all available categories
 */
export const getTemplateCategories = () => registry.getTemplateCategories();

/**
 * Validate a template configuration
 */
export const validateTemplate = (config: AnalyticsConfig) => registry.validateTemplate(config);

/**
 * Get the registry instance for advanced usage
 */
export const getTemplateRegistry = () => registry;

// Re-export types for convenience
export type { TemplateMetadata }; 